import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { crmApi } from '@/features/crm-quick-access/api/crmApi';
import {
	addDays,
	browserTimeZone,
	eventEnd,
	eventStart,
	meetingEvents,
	startOfDay
} from '@/features/crm-quick-access/utils/format';
import { markEventsSeen, readSeenEvents } from '@/features/crm-quick-access/utils/storage';

const MAIL_POLL_MS = 60 * 1000;
const CALENDAR_POLL_MS = 5 * 60 * 1000;
// How far ahead the meetings drawer looks; the CRM caps a range at 62 days.
const CALENDAR_FUTURE_DAYS = 60;
// Upcoming meetings inside this window count towards the badge.
const BADGE_WINDOW_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Runs `load` now, on an interval while the tab is visible, and whenever the tab regains focus. */
const usePolling = (load, intervalMs, enabled) => {
	const loadRef = useRef(load);
	useEffect(() => {
		loadRef.current = load;
	}, [load]);

	useEffect(() => {
		if (!enabled) return undefined;
		const run = () => {
			if (document.visibilityState === 'visible') loadRef.current();
		};
		run();
		const timer = window.setInterval(run, intervalMs);
		window.addEventListener('focus', run);
		document.addEventListener('visibilitychange', run);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener('focus', run);
			document.removeEventListener('visibilitychange', run);
		};
	}, [enabled, intervalMs]);
};

export const useMailFeed = (enabled, drawerOpen) => {
	const [account, setAccount] = useState(null);
	const [ready, setReady] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [threads, setThreads] = useState([]);
	const [threadsLoading, setThreadsLoading] = useState(false);
	const [error, setError] = useState(null);
	const drawerOpenRef = useRef(drawerOpen);
	useEffect(() => {
		drawerOpenRef.current = drawerOpen;
	}, [drawerOpen]);

	const loadThreads = useCallback(async () => {
		setThreadsLoading(true);
		try {
			const page = await crmApi.mailThreads({ wellKnownName: 'inbox', pageSize: 40 });
			setThreads(page?.items ?? []);
			setError(null);
		} catch (loadError) {
			setError(loadError.message || 'Unable to load the inbox.');
		} finally {
			setThreadsLoading(false);
		}
	}, []);

	// Pass `true` to also reload the thread list (e.g. when the drawer opens).
	const refresh = useCallback(
		async (includeThreads = drawerOpenRef.current) => {
			try {
				const nextAccount = await crmApi.mailAccount();
				setAccount(nextAccount);
				// Folders and threads 404 until a mailbox exists; a reauth-required
				// mailbox still has its synced mail, so keep showing it.
				if (!nextAccount || nextAccount.status === 'disconnected') {
					setUnreadCount(0);
					setThreads([]);
					return;
				}
				const folders = (await crmApi.mailFolders()) ?? [];
				setUnreadCount(folders.find((folder) => folder.wellKnownName === 'inbox')?.unreadCount ?? 0);
				if (includeThreads) await loadThreads();
			} catch (loadError) {
				setError(loadError.message || 'Unable to load mail.');
			} finally {
				setReady(true);
			}
		},
		[loadThreads]
	);

	usePolling(refresh, MAIL_POLL_MS, enabled);

	return { account, ready, unreadCount, threads, threadsLoading, error, refresh };
};

/** Events that can raise the badge: meetings starting soon and not yet over. */
const badgeCandidates = (events, now) => {
	const windowEnd = now + BADGE_WINDOW_DAYS * DAY_MS;
	return events.filter((event) => {
		const start = eventStart(event).getTime();
		const end = eventEnd(event)?.getTime() ?? start;
		return end >= now && start <= windowEnd;
	});
};

export const useCalendarFeed = (enabled) => {
	// Buyer visits and (pre-)selection meetings from today on (ongoing ones included), soonest first.
	const [events, setEvents] = useState([]);
	const [outlook, setOutlook] = useState(null);
	const [loading, setLoading] = useState(false);
	// Epoch ms of the last successful load; 0 until the first one.
	const [loadedAt, setLoadedAt] = useState(0);
	const [error, setError] = useState(null);
	const [seen, setSeen] = useState(() => readSeenEvents());

	const refresh = useCallback(async () => {
		const today = startOfDay(new Date());
		setLoading(true);
		try {
			const response = await crmApi.calendarEvents({
				from: today.toISOString(),
				to: addDays(today, CALENDAR_FUTURE_DAYS).toISOString(),
				timezone: browserTimeZone()
			});
			setEvents(meetingEvents(response?.events ?? []));
			setOutlook(response?.outlook ?? null);
			setLoadedAt(Date.now());
			setError(null);
		} catch (loadError) {
			setError(loadError.message || 'Unable to load the calendar.');
		} finally {
			setLoading(false);
		}
	}, []);

	usePolling(refresh, CALENDAR_POLL_MS, enabled);

	const candidates = useMemo(() => badgeCandidates(events, loadedAt), [events, loadedAt]);
	const unseenCount = useMemo(
		() => candidates.filter((event) => !(event.id in seen)).length,
		[candidates, seen]
	);

	const markAllSeen = useCallback(() => {
		if (candidates.length === 0) return;
		setSeen(
			markEventsSeen(
				candidates.map((event) => ({
					id: event.id,
					expiresAt: (eventEnd(event) ?? eventStart(event)).getTime() + DAY_MS
				}))
			)
		);
	}, [candidates]);

	return { events, outlook, loading, loadedAt, error, unseenCount, markAllSeen, refresh };
};
